<template>
  <div class="TextInput">
    <div class="flex align-items-center">

      <div class="color-integrated-group" :style="dynamicGroupStyle">

        <div class="picker-wrapper">
          <pick-colors v-model:value="colorSync" :format="format" :format-options="formatOptions" :z-index="9999"
            show-alpha />
        </div>

        <div v-if="inputValue" class="code-interactive-area" @dblclick="copyToClipboard">
          <span class="mono-text">{{ formattedDisplayValue }}</span>

          <button type="button" class="inner-copy-btn" @click.stop="copyToClipboard">
            <Icon type="favicon" :icon="'Copy'" class="w-14 h-4" />
          </button>
        </div>
      </div>

    </div>

    <small v-if="errorMessage" class="p-error">{{ errorMessage }}</small>
  </div>
</template>

<script>
import { ref, computed, toRef, watch } from "vue";
import { useField } from "vee-validate";
import { useToast } from "primevue/usetoast";
import pickColors from 'vue-pick-colors';

export default {
  name: "BaseColorPicker",
  components: { 'pick-colors': pickColors },
  props: {
    name: { type: String, required: true },
    modelValue: { type: [String, Number], default: "" },
    value: { type: String, default: "" },
    format: { type: String, default: 'hex' }
  },
  emits: ['update:modelValue', 'colorChange'],

  setup(props, { emit }) {
    const toast = useToast();
    const formatOptions = ref(['rgb', 'hex', 'hsl', 'hsv']);

    const { value: inputValue, errorMessage, handleChange } = useField(toRef(props, 'name'), undefined, {
      initialValue: props.modelValue || props.value,
    });

    watch(() => props.modelValue, (newVal) => {
      if (newVal !== inputValue.value) { inputValue.value = newVal; }
    });

    const colorSync = computed({
      get() { return inputValue.value; },
      set(newValue) {
        handleChange(newValue);
        emit('update:modelValue', newValue);
        emit('colorChange', { name: props.name, value: newValue });
      }
    });

    const formattedDisplayValue = computed(() => {
      const val = inputValue.value;
      if (!val) return '';
      return (props.format === 'hex' && !val.startsWith('#')) ? `#${val}` : val;
    });

    // Dynamic background: creates a very soft "aura" of the color
    const dynamicGroupStyle = computed(() => {
      if (!inputValue.value) return {};
      return {
        borderColor: `${inputValue.value}66`, // 40% opacity border
        boxShadow: `0 0 10px ${inputValue.value}1A` // 10% opacity glow
      };
    });

    const copyToClipboard = () => {
      const text = formattedDisplayValue.value;
      if (!text) return;
      navigator.clipboard.writeText(text).then(() => {
        toast.add({ severity: 'success', summary: 'Copied', detail: text, life: 2000 });
      });
    };

    return { colorSync, inputValue, errorMessage, formatOptions, copyToClipboard, formattedDisplayValue, dynamicGroupStyle };
  }
};
</script>

<style scoped>
.color-integrated-group {
  display: flex;
  align-items: center;
  background: var(--p-content-background);
  border: 1px solid var(--p-content-border-color);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.picker-wrapper {
  padding: 4px;
  border-right: 1px solid var(--p-content-border-color);
  background: var(--p-surface-50);
  display: flex;
}

/* Customizing the library's internal trigger */
:deep(.vue-pick-colors .color-picker-trigger) {
  border-radius: 4px !important;
  border: none !important;
  width: 28px !important;
  height: 28px !important;
}

.code-interactive-area {
  position: relative;
  padding: 0 12px;
  height: 36px;
  display: flex;
  align-items: center;
  min-width: 120px;
  cursor: pointer;
}

.mono-text {
  font-family: var(--font-family-monospace, monospace);
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--p-text-color);
  text-transform: uppercase;
  transition: opacity 0.2s;
}

.inner-copy-btn {
  position: absolute;
  right: 8px;
  opacity: 0;
  background: var(--p-primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  transform: translateX(5px);
}

.code-interactive-area:hover .mono-text {
  opacity: 0.3;
  /* Fade text to show copy button clearly */
}

.code-interactive-area:hover .inner-copy-btn {
  opacity: 1;
  transform: translateX(0);
}

.p-error {
  color: var(--red-500);
  font-size: 0.8rem;
  margin-top: 5px;
}
</style>